import { Box, Divider, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { useCallback } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { ManagersFilter } from "@/components/common/Filters/ManagersFilter"
import { StatusFilter } from "@/components/common/Filters/StatusFilter"
import type { CompanyListFilter } from "@/graphql/codegen/graphql"
import { CompanyStatus } from "@/graphql/codegen/graphql"
import { CompanyFilterSchema } from "@/screens/Companies/types/filters"
import { getRefinedSearchParams } from "@/utils/getRefinedSearchParams"

const statusList = Object.values(CompanyStatus)
const statusKey = "statuses"

export const SupplierReportFilter: FC = () => {
  const navigate = useNavigate({ from: "/app/procurement/_layout/supplier-report" })
  const currentSearch = useSearch({
    from: "/app/procurement/_layout/supplier-report",
    strict: true,
  })

  const { statuses = [], managers = [], types = [] } = currentSearch

  const methods = useForm<CompanyListFilter>({
    values: {
      statuses,
      managers,
      types,
    },
    defaultValues: CompanyFilterSchema.parse({
      statuses: statuses ?? [],
      managers: managers ?? [],
      types: types ?? [],
    }),
  })

  const handleFilterChange = useCallback(
    (name: keyof CompanyListFilter, value: any) => {
      const params = getRefinedSearchParams(currentSearch, {
        ...currentSearch,
        [name]: value,
      })

      void navigate(params)
    },
    [currentSearch, navigate]
  )

  return (
    <FormProvider {...methods}>
      <Box className='flex h-full flex-col gap-y-2'>
        <Box className='flex-none'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Company Status
          </Typography>
          <Divider />
          <StatusFilter
            name={statusKey}
            data={statusList}
            control={methods.control}
            onFilterChange={handleFilterChange}
          />
        </Box>

        <Box className='grow overflow-hidden'>
          <ManagersFilter onFilterChange={handleFilterChange} />
        </Box>

        {/* TODO: add classification filter; BE is not ready yet */}
      </Box>
    </FormProvider>
  )
}
